import "./style.css";
import TickImg from "../../assets/images/tick.svg";
import { useEffect, useState } from "react";
import TabsDialog from "../tabsDialog";
import { envConfig } from "../../utils/constants";
import fetchDataFromTelligent from "../commonFunction";
import { useAppSelector } from "../../hooks";
import { selectStoreConfigSelector } from "../../store/storeConfig/selector";
import { formatNumberWithCommas, mtm_getUtmParamsFromSession } from "../../utils/utility";

export interface CardLayoutProps {
  id: number;
  price: number;
  mvp: number;
  custom: number;
  customPriceInfo?: string;
  customPackageLink?: string;
  name: string;
  description: string;
  currency: string;
  suffix: string;
  termDescription: string;
  features: Array<string>;
  selectCard: Function;
  trial_message_summarycard?: string;
  isSelected: string;
  sku: string;
  btnText: string;
  trial_duration: number;
  loader: boolean;
  maxLengthOfFeatures: number;
  virtualProductSku?: string;
  showTelligentData?: boolean;
}

interface ProductData {
  pid: string;
  images: string[];
  video: string | null;
  name: string;
  shortdescription: string;
  detaileddescription: string;
  keybenefits: string;
  productname: string;
}

const CardLayout = (props: CardLayoutProps) => {
  const {
    isSelected,
    btnText,
    id,
    mvp,
    custom,
    customPriceInfo,
    customPackageLink,
    price,
    name,
    currency,
    description,
    suffix,
    features,
    termDescription,
    trial_duration,
    trial_message_summarycard,
    sku,
    selectCard,
    maxLengthOfFeatures,
    virtualProductSku,
    showTelligentData,
  } = props;

  const parentHeight = maxLengthOfFeatures <= 4 ? "620px" : "710px";
  const containerHeight = maxLengthOfFeatures <= 4 ? "574px" : "665px";
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [telligentData, setTelligentData] = useState<ProductData[]>([]);
  const storeData = useAppSelector(selectStoreConfigSelector);
  const [tabsData, setTabsData] = useState<any[]>([]);

  useEffect(() => {
    if (virtualProductSku === "") return;
    storeData?.telligent_api_enabled === "1" && fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const matched = JSON.parse(storeData.telligent_products_json || "{}").telligent_product_details.find(
        (item: any) => item.package_sku === sku
      );
      const telligentIds: string[] = matched.telligent_product_ids.split(",").map((id: string) => id.trim());
      const skuList = telligentIds.join(",");
      const response = await fetchDataFromTelligent(skuList, storeData?.telligent_api_url);
  
      if (response) {
        const productData = response.map((item: any) => ({
          pid: item?.pid,
          images: item?.images || [],
          video: item?.video || null,
          name: item?.name,
          shortdescription: item?.shortdescription || "",
          detaileddescription: item?.detaileddescription || "",
          keybenefits: item?.keybenefits || "",
          productname: item?.productname || "",
        }));

        setTelligentData(productData);

        const newTabsData = productData.map(
          (item: ProductData, index: number) => ({
            name: item.productname,
            value: `Tab ${index + 1}`,
            features: [item.keybenefits],
          })
        );

        setTabsData(newTabsData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSeeDetailsClick = () => {
    setDialogOpen(true);
  };

  return (
    <div
      className={`card-parent ${mvp ? "mvp-highlighter" : ""}`}
      style={{ height: parentHeight }}
    >
      {mvp ? <span className="mvp-title"> Most Popular</span> : ""}
      <div
        key={name}
        style={{ height: containerHeight }}
        className={`card-container text-blues-ink bg-white w-full py-5 flex flex-col items-center justify-between border border-grey-scale-sleet border-solid rounded-2xl`}
      >
        <div className="card-header flex flex-col items-center text-center border-b border-grey-scale-sleet border-solid see-details">
          <span
            className="card-header-title m-0 "
            dangerouslySetInnerHTML={{
              __html: name.replace(
                /Nexis\+ AI/g,
                "Nexis+&nbsp;<span>AI</span>"
              ),
            }}
          />
          <p
            className="card-header-description text-base font-normal pt-0 px-0 leading-none md:leading-5 text-heading"
            dangerouslySetInnerHTML={{ __html: description }}
          />
          <span>
            {showTelligentData && custom === 0 ? (
              <button
                className="text-blues-denim"
                onClick={handleSeeDetailsClick}
                data-track="see-details-button"
              >
                <strong>See Details</strong>
              </button>
            ) : null}
          </span>
        </div>

        <div>
          {dialogOpen && (
            <TabsDialog
              open={dialogOpen}
              handleClose={() => setDialogOpen(false)}
              title={name}
              subtitle="Includes all products shown below"
              currency={currency}
              price={price}
              suffix={suffix}
              showFooter={true}
              submitTitle="Select"
              cancelTitle="Cancel"
              tabsData={tabsData}
              selectedItems={selectedItems}
              selectItem={(item: any) => {
                setSelectedItems((prevSelected) =>
                  prevSelected.includes(item)
                    ? prevSelected.filter((selected) => selected !== item)
                    : [...prevSelected, item]
                );
              }}
              virtualProductSku={virtualProductSku}
              telligentData={telligentData}
              checkout={() => selectCard(id.toString())}
            />
          )}
        </div>

        <div className="card-body flex items-center flex-col">
          {custom === 0 ? (
            <>
              <span className="font-bold text-[2rem] text-end">
                {currency}
                {formatNumberWithCommas(price)}
                <sup className="text-lg text-grey-scale-slate font-light align-sub">
                  /{suffix}
                </sup>
              </span>
              <span className="font-semibold text-base text-gray-scale-slate">
                {termDescription}
              </span>
            </>
          ) : (
            <>
              <h3 className="font-bold text-[2rem] text-end">
                {customPriceInfo}
              </h3>
            </>
          )}

          <div className="mt-2 w-full">
            {features &&
              features.map((feature: string, index: number) => {
                const trimmedFeature = feature.trim();

                if (trimmedFeature === "All Nexis Essential content plus") {
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-start w-full"
                    >
                      <span
                        className="list-feature-item"
                        dangerouslySetInnerHTML={{
                          __html: `${trimmedFeature}:`,
                        }}
                      />
                    </div>
                  );
                }

                return (
                  <div
                    key={index}
                    className="flex items-center justify-start w-full"
                  >
                    <img src={TickImg} alt="Tick" />
                    <span
                      className="list-feature-item ml-2"
                      dangerouslySetInnerHTML={{ __html: trimmedFeature }}
                    />
                  </div>
                );
              })}
          </div>
        </div>

        <div className="card-footer">
          {custom === 0 ? (
            <>
              <button
                aria-label={isSelected === sku ? "Currently Selected" : btnText}
                onClick={() => selectCard(id.toString())}
                className="transition duration-2000 ease-out card-action-btn w-full h-12 bg-blues-midnight text-white text-base font-bold cursor-pointer border-0 rounded-md mb-3 hover:bg-blues-ink"
                data-track={`select-button-${id}`}
              >
                {isSelected === sku ? "Currently Selected" : btnText}
              </button>
              {trial_duration > 0 && (
                <span className="text-sm text-grey-scale-slate">
                  {trial_message_summarycard}
                </span>
              )}
            </>
          ) : (
            <a
              href={
                mtm_getUtmParamsFromSession()
                  ? `${customPackageLink}${mtm_getUtmParamsFromSession()}`
                  : customPackageLink
              }
              target="_blank"
              rel="noreferrer"
              data-track={`custom-package-link-${id}`}
            >
              <button
                aria-label="Custom Package"
                className="transition duration-2000 ease-out card-action-btn w-full h-12 bg-blues-midnight text-white text-base font-bold cursor-pointer border-0 rounded-md mb-3 hover:bg-blues-ink"
                data-track={`custom-package-button-${id}`}
              >
                Request a Quote
              </button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardLayout;
